import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Form,
  FormInstance,
  Spin,
  Typography,
  notification,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import classNames from 'classnames';
import { useEmployee } from 'hooks';
import { useSaveInterests } from 'user/blocks/interests-block/query';
import { InterestsTips } from './interests-tips';
import styles from './styles.less';

export type InterestsFormProps = {
  editMode: boolean;
  formName: string;
  form: FormInstance;
  setIsEditMode: (editMode: boolean) => void;
};

export const InterestsForm: FC<InterestsFormProps> = ({
  editMode,
  formName,
  form,
  setIsEditMode,
}) => {
  const { employee, error, loading } = useEmployee();
  const { t } = useTranslation();

  const saveInterests = useSaveInterests();

  if (loading) {
    return <Spin />;
  }

  if (error || !employee) {
    return <Alert type="error" message={error?.message} />;
  }

  const interests = employee.interests;

  const children = !editMode ? (
    <Typography.Text
      className={classNames(
        interests ? styles['interests-text'] : styles['placeholder-text'],
      )}
    >
      {interests ||
        t(
          'Tell your colleagues about your hobbies and interests. What do you like to do in your free time?',
        )}
    </Typography.Text>
  ) : (
    <Form.Item name={'text'} label={'What are your hobbies and interests?'}>
      <TextArea
        maxLength={2000}
        placeholder={
          'Tell your colleagues about your hobbies and interests. What do you like to do in your free time?'
        }
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        autoSize={{ minRows: 5 }}
      />
    </Form.Item>
  );

  const onSubmit = ({ text }: { text: string }): void => {
    saveInterests(text)
      .then(() =>
        notification.success({
          message: 'Interests have been saved!',
        }),
      )
      .catch(() =>
        notification.success({
          message: 'Something has gone wrong. Try again!',
        }),
      )
      .finally(() => setIsEditMode(false));
  };

  return (
    <>
      <Form
        form={form}
        name={formName}
        onFinish={onSubmit}
        initialValues={{
          text: interests,
        }}
        layout="vertical"
      >
        {children}
      </Form>
      {editMode && <InterestsTips />}
    </>
  );
};
